import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Trophy, Search, Send, Eye, Handshake, Loader2 } from "lucide-react";
import api from "@/lib/api";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const STATS = [
  ["scans", "Scans", Search, "scan"],
  ["proposals_sent", "Proposals sent", Send, "sent"],
  ["views", "Prospect views", Eye, "view"],
  ["deals_won", "Deals won", Handshake, "won"],
];

export const RepScoreCard = () => {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/api/webadvisor/my-score?week_offset=${offset}`).then(r => setData(r.data)).catch(() => setData(false));
  }, [offset]);

  if (data === false) return null;
  const top = data && data.rank === 1 && data.me.points > 0;
  return (
    <div className="bg-slate-900 text-white rounded-xl p-5 mb-6 shadow-sm" data-testid="rep-score-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Trophy className={`w-5 h-5 ${top ? "text-yellow-400" : "text-slate-400"}`} />
          <p className="font-bold">Your weekly score</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setOffset(o => o - 1)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20" data-testid="score-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-xs font-medium min-w-[130px] text-center text-slate-300" data-testid="score-week-label">{data ? `${offset === 0 ? "This week · " : ""}${fmt(data.week_start)} – ${fmt(data.week_end)}` : "…"}</span>
          <button onClick={() => setOffset(o => Math.min(0, o + 1))} disabled={offset === 0} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="score-next-week"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {!data ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-1 md:col-span-1 bg-blue-600 rounded-lg p-3" data-testid="score-points">
            <p className="text-[10px] uppercase tracking-wider text-blue-100 font-bold">Points</p>
            <p className="text-3xl font-black">{data.me.points}</p>
          </div>
          <div className="col-span-1 md:col-span-1 bg-white/10 rounded-lg p-3" data-testid="score-rank">
            <p className="text-[10px] uppercase tracking-wider text-slate-300 font-bold">Rank</p>
            <p className="text-3xl font-black">{data.me.points > 0 ? `#${data.rank}` : "—"}<span className="text-sm font-medium text-slate-400"> of {data.total_reps}</span></p>
          </div>
          {STATS.map(([key, label, Icon, pk]) => (
            <div key={key} className="bg-white/5 rounded-lg p-3" data-testid={`score-${key}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Icon className="w-3 h-3" />{label}</p>
              <p className="text-xl font-bold">{data.me[key]} <span className="text-xs font-normal text-slate-400">· {data.me[key] * data.points[pk]} pts</span></p>
            </div>
          ))}
        </div>
      )}
      {data && offset === 0 && (
        <p className="text-xs text-slate-400 mt-3" data-testid="score-hint">
          {top ? "You're leading the team this week — keep it up!" : data.points_to_next_rank > 0 ? `${data.points_to_next_rank} more points to move up a rank. A won deal is worth ${data.points.won}.` : `Scan a prospect (+${data.points.scan}) or send a proposal (+${data.points.sent}) to get on the board.`}
        </p>
      )}
    </div>
  );
};
